/**
 * YouTube (API routes/youtube.ts, docs/design/youtube-upload-2026-10-21.md; the redesign of
 * 2026-10-22 in docs/design/mocks/youtube/). The user's uploaded video itself is posted to their
 * channel, not clips: "Post to YouTube" is a switch on the Upload page, sent with the upload, and the
 * post starts once the check after the upload passes. A user connects their channel once, in a new
 * window ("Connect" must not leave the Upload page, or the chosen file is lost): the window goes
 * through the API to Google's consent page and comes back to /youtube/done, which tells this page the
 * result and closes.
 */
import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { get } from "../api/client";
import { useLoad, type Loaded } from "../api/hooks";
import type { YoutubePrivacy, YoutubeStatus, YoutubeUpload } from "../api/types";

/** YouTube refuses a longer title (characters) or description (UTF-8 bytes), and < or > in either. */
export const YT_TITLE_MAX = 100;
export const YT_DESCRIPTION_MAX_BYTES = 5000;
/** A channel that is not verified on YouTube cannot post a video longer than this. */
export const YT_UNVERIFIED_MAX_S = 15 * 60;

export const PRIVACY: { value: YoutubePrivacy; label: string; note: string }[] = [
  { value: "private", label: "Private", note: "Only you can see it." },
  { value: "unlisted", label: "Unlisted", note: "Anyone with the link can see it. It is not listed on your channel." },
  { value: "public", label: "Public", note: "Anyone can find it and see it." },
];

const OFF: YoutubeStatus = { enabled: false, connection: null };

/**
 * Whether YouTube is set up here, and the user's channel; nothing is asked when `on` is false (the
 * deployment has YouTube off). `refresh()` has the API read the channel from YouTube again, for a
 * channel verified since (the API reads it at most every 30 s).
 */
export function useYoutube(on = true): Loaded<YoutubeStatus> & { refresh: () => Promise<void> } {
  const status = useLoad(() => (on ? get<YoutubeStatus>("/v1/youtube").catch(() => OFF) : Promise.resolve(OFF)), [on]);
  const { set } = status;
  const refresh = useCallback(async () => {
    if (!on) return;
    const next = await get<YoutubeStatus>("/v1/youtube?refresh=1").catch(() => null);
    if (next) set(next);
  }, [on, set]);
  return { ...status, refresh };
}

const DONE_PATH = "/youtube/done";
const CHANNEL = "dt-youtube";
const STORE_KEY = "dt.youtube.connect";

/**
 * Open Google's consent page in a new window; it comes back to /youtube/done, which reports the
 * result to `useConnectResult`. A browser that blocks the window gets the same page in this one,
 * coming back here with `?youtube=` (`useYoutubeReturn`).
 */
export function openYoutubeConnect(): void {
  const w = window.open(`/v1/youtube/connect?return=${encodeURIComponent(DONE_PATH)}`, "deepthird-youtube", "popup,width=520,height=720");
  if (!w) window.location.assign(`/v1/youtube/connect?return=${encodeURIComponent(stripResult(window.location.pathname + window.location.search))}`);
}

function stripResult(path: string): string {
  const [p, q = ""] = path.split("?");
  const params = new URLSearchParams(q);
  params.delete("youtube");
  const rest = params.toString();
  return rest ? `${p}?${rest}` : p!;
}

const RESULT: Record<string, string> = {
  connected: "YouTube is connected.",
  denied: "YouTube was not connected: it was cancelled on Google's page.",
  scope: "YouTube was not connected. On Google's page, allow both: uploading videos and seeing your channel.",
  no_channel: "This Google account has no YouTube channel. Create one on YouTube, then connect again.",
  expired: "That link to YouTube expired. Connect again.",
  error: "YouTube could not be connected. Try again.",
  off: "Posting to YouTube is not available yet.",
};

export function connectMessage(result: string): string {
  return RESULT[result] ?? RESULT["error"]!;
}

/** /youtube/done: tell the page that opened the window, by a BroadcastChannel and by storage (which every browser has). */
export function announceConnectResult(result: string): void {
  const msg = { result, at: Date.now() };
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.postMessage(msg);
    bc.close();
  } catch {
    // no BroadcastChannel: the storage event below carries it
  }
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(msg));
  } catch {
    // storage blocked: the opener still reads the channel again when its window has the focus back
  }
}

/** The result a connect window reports, once each; and the window coming back into focus (`onFocus`), when it may not have reported. */
export function useConnectResult(onResult: (result: string) => void, onFocus?: () => void): void {
  const seen = useRef(0);
  const cb = useRef(onResult);
  const focus = useRef(onFocus);
  cb.current = onResult;
  focus.current = onFocus;
  useEffect(() => {
    const take = (raw: unknown) => {
      const m = raw as { result?: unknown; at?: unknown } | null;
      if (!m || typeof m.result !== "string" || typeof m.at !== "number" || m.at <= seen.current) return;
      seen.current = m.at;
      cb.current(m.result);
    };
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(CHANNEL);
      bc.onmessage = (e) => take(e.data);
    } catch {
      bc = null;
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORE_KEY || !e.newValue) return;
      try {
        take(JSON.parse(e.newValue));
      } catch {
        // not ours
      }
    };
    const onWindowFocus = () => focus.current?.();
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onWindowFocus);
    return () => {
      bc?.close();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onWindowFocus);
    };
  }, []);
}

/**
 * The `?youtube=` a same-window connect came back with (the browser blocked the new window): said
 * once, then taken out of the address, after the page's own effects have run, from the address as
 * they left it.
 */
export function useYoutubeReturn(say: (msg: string) => void, onConnected?: () => void): void {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const result = params.get("youtube");
  useEffect(() => {
    if (!result) return;
    say(connectMessage(result));
    if (result === "connected") onConnected?.();
    const t = setTimeout(() => {
      const next = new URLSearchParams(window.location.search);
      if (!next.has("youtube")) return;
      next.delete("youtube");
      const q = next.toString();
      navigate({ pathname: window.location.pathname, search: q ? `?${q}` : "" }, { replace: true });
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);
}

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

/** What YouTube would refuse in a title or description, in one sentence; null when it takes both. */
export function fieldProblem(title: string, description: string): string | null {
  if (!title.trim()) return "Give the video a title for YouTube.";
  if ([...title.trim()].length > YT_TITLE_MAX) return `YouTube takes titles of up to ${YT_TITLE_MAX} characters.`;
  if (/[<>]/.test(title) || /[<>]/.test(description)) return "YouTube does not take < or > in a title or description.";
  if (byteLength(description.trim()) > YT_DESCRIPTION_MAX_BYTES) return "The description is longer than YouTube takes.";
  return null;
}

/** A post that may still change on its own: its row is asked again (waiting for midnight is asked rarely). */
export function uploadMoving(u: YoutubeUpload | null | undefined): boolean {
  return !!u && (u.state === "waiting_check" || u.state === "waiting_export" || u.state === "queued" || u.state === "uploading");
}
